/**
 * Small UI atoms shared by the Manual Trading Hub.
 *
 * Extracted from the monolithic Meta5ManualTrading.jsx during a refactor.
 * These components are pure/presentational — no state ownership.
 */
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-white/50 uppercase tracking-wider">{label}</span>
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
      </div>
      <div className="text-lg font-black" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export function FieldPair({ label, value, onChange, type = 'text', placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-white/60">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-black/40 border-white/10 text-white"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * Small dual-input used by the "Modify TP/SL" dialog. Same UX as the main
 * order form (Price / $ toggle) but scoped to a plain form-state object
 * (no live tick preview since we don't have per-position live prices in the
 * dialog). Emits the raw number to the parent — the parent converts $-mode
 * to absolute price using the average open-price of matching positions.
 */
export function TpSlDualInput({ kind, label, accent, form, setForm, isAr }) {
  const mode = form[kind + 'Mode'] || 'price';
  const setMode = (m) => setForm({ ...form, [kind + 'Mode']: m });
  const value = form[kind] || '';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className={`text-xs font-bold ${accent}`}>{label}</Label>
        <div
          dir="ltr"
          className="relative flex items-center p-0.5 rounded-md bg-black/50 border border-white/15 shadow-inner"
        >
          <div
            className="absolute top-0.5 bottom-0.5 rounded bg-[#d4af37] shadow-[0_0_8px_rgba(212,175,55,0.35)] transition-all duration-200 ease-out"
            style={{
              width: 'calc(50% - 2px)',
              left: mode === 'price' ? '2px' : 'calc(50% + 0px)',
            }}
            aria-hidden
          />
          <button
            type="button"
            onClick={() => setMode('price')}
            className={`relative z-10 min-w-[38px] px-2 py-0.5 rounded text-[10px] font-black transition-colors duration-150 ${
              mode === 'price' ? 'text-black' : 'text-white/60'
            }`}
            data-testid={`tpsl-${kind}-mode-price`}
          >
            {isAr ? 'سعر' : 'Price'}
          </button>
          <button
            type="button"
            onClick={() => setMode('distance')}
            className={`relative z-10 min-w-[28px] px-2 py-0.5 rounded text-[11px] font-black leading-none transition-colors duration-150 ${
              mode === 'distance' ? 'text-black' : 'text-white/60'
            }`}
            data-testid={`tpsl-${kind}-mode-distance`}
          >
            $
          </button>
        </div>
      </div>
      <Input
        type="number"
        value={value}
        onChange={(e) => setForm({ ...form, [kind]: e.target.value })}
        placeholder={
          mode === 'distance'
            ? (isAr ? 'مسافة $ (مثال: 3)' : 'distance $ (e.g. 3)')
            : (isAr ? 'اتركه فارغ' : 'blank = keep')
        }
        className="bg-black/40 border-white/10 text-white h-9 text-sm"
        data-testid={`tpsl-${kind}`}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Section Card — standalone box wrapping a data table
// ────────────────────────────────────────────────────────
// Optionally supports an action element in the header (right side) — e.g. a
// "Modify all TP/SL" button on the Positions section.
export function SectionCard({ title, icon: Icon, badge, testId, children, action }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.02]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-white/10">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-[#d4af37]" />}
          <h3 className="text-sm font-black text-white">{title}</h3>
          {badge != null && badge !== 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-black bg-[#d4af37]/25 text-[#d4af37] border border-[#d4af37]/40 flex items-center justify-center">
              {badge}
            </span>
          )}
        </div>
        {action}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Tabbed Section — N tabs inside one box (Accounts / Positions / Pending / Templates)
// ────────────────────────────────────────────────────────
export function TabbedSection({ tabs, active, onChange, action, testId }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.02]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                active === t.id
                  ? 'bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40'
                  : 'text-white/60 hover:text-white hover:bg-white/[0.05] border border-transparent'
              }`}
              data-testid={`${testId}-tab-${t.id}`}
            >
              {t.icon && <t.icon className="h-3.5 w-3.5" />}
              <span>{t.label}</span>
              {t.badge != null && t.badge !== 0 && (
                <span className={`min-w-[18px] h-4 px-1 rounded-full text-[9px] font-black flex items-center justify-center ${
                  active === t.id
                    ? 'bg-[#d4af37]/30 text-[#d4af37]'
                    : 'bg-white/10 text-white/70'
                }`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        {action}
      </div>
      <div className="p-3">
        {tabs.find((t) => t.id === active)?.content}
      </div>
    </div>
  );
}
